import React, { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';
import { Box } from '@mui/material';
import { resolveSlotValues, getDTWidgetSlots } from '../../registry/dtWidgetTypes';
import { useDTDemoMap } from '../../hooks/useDTDemoTick';
import { hasLiveSlotValue } from '../../utils/dtDemoFallback';
import { useContainerSize } from '../../hooks/useContainerSize';
import { temperatureTrendOptions } from '../../../constants';

const DEFAULT_SLOTS = getDTWidgetSlots('dt-temperature-trend');
const SERIES_COLORS = ['#FF5733', '#FFD000', '#008CD7'];

/** ~1h span, enough points that the line renders clearly on the chart */
const TREND_POINT_COUNT = 18;
const TREND_STEP_MS = 4 * 60 * 1000;

function strHash(str) {
  let h = 0;
  for (let i = 0; i < str.length; i += 1) {
    h = (Math.imul(31, h) + str.charCodeAt(i)) | 0;
  }
  return h >>> 0;
}

function mulberry32(seed) {
  return () => {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Past points interpolate from a seeded start and end exactly on {@link latestY} at {@link endTs}. */
function buildTrendDatapoints(latestY, endTs, seedKey) {
  const rnd = mulberry32((strHash(seedKey) + strHash(String(Math.round(latestY * 100)))) >>> 0);
  const n = TREND_POINT_COUNT;
  const startY = latestY + (rnd() - 0.5) * 12;
  const points = [];
  for (let i = 0; i < n; i += 1) {
    const t = n <= 1 ? 1 : i / (n - 1);
    const x = endTs - (n - 1 - i) * TREND_STEP_MS;
    const smooth = startY + (latestY - startY) * t;
    const ripple = Math.sin(t * Math.PI * 2.2) * 2.8 + (rnd() - 0.5) * 1.8;
    const damp = 1 - t * 0.35;
    const y = i === n - 1 ? latestY : Number((smooth + ripple * damp).toFixed(2));
    points.push({ x, y });
  }
  return points;
}

function DTTemperatureTrend({ widget, data, theme }) {
  const isDark = theme === 'dark';
  const tagprops = widget?.props?.extended?.tagprops || [];
  const liveData = Array.isArray(data) ? data : [];
  const demoMap = useDTDemoMap('dt-temperature-trend');

  const revisionKey = `${demoMap.temp1}|${demoMap.temp2}|${demoMap.temp3}`;

  const activeSeries = useMemo(() => {
    const slotValues = resolveSlotValues(tagprops, liveData);
    const now = Date.now();

    const buildOne = (slot, alias, demoValSeed) => {
      const resolved = slotValues[slot.key];
      const name = resolved?.alias || alias;
      const liveOk = hasLiveSlotValue(resolved);
      const raw = liveOk ? parseFloat(resolved.value) : Number(demoMap[slot.key]);
      if (!Number.isFinite(raw)) return null;
      const seedKey = `${slot.key}:${liveOk ? String(resolved.value) : revisionKey}:${demoValSeed}`;
      return {
        name,
        data: buildTrendDatapoints(raw, now, seedKey),
      };
    };

    const built = DEFAULT_SLOTS.map((slot, i) => buildOne(slot, slot.label, i)).filter(Boolean);
    if (built.length > 0) return built;

    const v = Number(demoMap.temp1);
    const y = Number.isFinite(v) ? v : 65;
    return [
      {
        name: DEFAULT_SLOTS[0]?.label || 'Temp 1',
        data: buildTrendDatapoints(y, now, `fallback:${revisionKey}`),
      },
    ];
  }, [tagprops, liveData, demoMap, revisionKey]);

  const chartKey = revisionKey + activeSeries.map((s) => s.data[s.data.length - 1]?.y).join('|');
  const { ref: chartWrapRef, width: cw, height: ch } = useContainerSize(100, 80);
  const plotW = Math.max(80, cw);
  const plotH = Math.max(72, ch);

  const dynamicOptions = useMemo(() => ({
    ...temperatureTrendOptions,
    colors: SERIES_COLORS.slice(0, activeSeries.length),
    theme: { mode: isDark ? 'dark' : 'light' },
    chart: {
      ...temperatureTrendOptions.chart,
      background: 'transparent',
      width: plotW,
      height: plotH,
    },
  }), [activeSeries, isDark, plotW, plotH]);

  return (
    <Box sx={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box ref={chartWrapRef} sx={{ flex: 1, minHeight: 0, width: '100%', minWidth: 0 }}>
        <ReactApexChart
          key={chartKey}
          options={dynamicOptions}
          series={activeSeries}
          type="line"
          height={plotH}
          width={plotW}
        />
      </Box>
    </Box>
  );
}

export default React.memo(DTTemperatureTrend);
